/**
 * Done. / ChiroCPD — Landing Page
 *
 * Shown to unauthenticated users. Provides subscription checkout via Stripe.
 * This is the entry point for new and returning practitioners.
 */

import { useState } from 'react';
import { BrandFooter } from '@/components/ui/BrandFooter';

export function LandingPage(): React.ReactElement {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubscribe(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/stripe/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({ error: 'Failed to create checkout' }));
        throw new Error(data.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-[var(--surface-subtle)]">
      <main
        id="main-content"
        className="flex flex-1 flex-col items-center justify-center gap-8 px-4 text-center"
      >
        {/* Logo / Brand */}
        <div className="flex flex-col items-center gap-2">
          <h1 className="text-4xl font-bold tracking-tight text-[var(--text-primary)]">
            ChiroCPD
          </h1>
          <p className="text-lg text-[var(--text-secondary)]">
            CPD Compliance Engine for NZ Chiropractors
          </p>
        </div>

        {/* Value prop */}
        <div className="max-w-md space-y-4">
          <p className="text-[var(--text-secondary)]">
            Track your CPD hours, generate AI-drafted reflections, and export directly
            to the Board portal — all in under 5 minutes.
          </p>

          <ul className="space-y-2 text-left text-sm text-[var(--text-secondary)]">
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-[var(--color-done-500)]">✓</span>
              <span>20hr/year &amp; 50hr/2-year cycle tracking with cap enforcement</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-[var(--color-done-500)]">✓</span>
              <span>AI-generated Annual Plan &amp; Outcomes/Reflections</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-[var(--color-done-500)]">✓</span>
              <span>Copy-paste export matching all 8 Board portal fields</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-[var(--color-done-500)]">✓</span>
              <span>Audit-ready compliance dashboard</span>
            </li>
          </ul>
        </div>

        {/* CTA */}
        <div className="flex flex-col items-center gap-3">
          <button
            onClick={handleSubscribe}
            disabled={loading}
            className="rounded-lg bg-[var(--color-done-600)] px-8 py-3 text-base font-semibold text-white shadow-sm transition-colors hover:bg-[var(--color-done-700)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-done-600)] disabled:opacity-50 disabled:cursor-not-allowed"
            aria-busy={loading}
          >
            {loading ? 'Redirecting to checkout…' : 'Subscribe — $85/month'}
          </button>
          <p className="text-xs text-[var(--text-muted)]">
            Cancel anytime. No lock-in contract.
          </p>
          {error && (
            <p role="alert" className="text-sm text-red-600">
              {error}
            </p>
          )}
        </div>

        {/* Already subscribed */}
        <p className="text-xs text-[var(--text-muted)]">
          Already subscribed?{' '}
          <button
            onClick={() => window.location.reload()}
            className="text-[var(--color-done-600)] hover:underline"
          >
            Refresh to check your session
          </button>
        </p>
      </main>

      <BrandFooter />
    </div>
  );
}
